import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  BriefcaseBusiness,
  CalendarClock,
  CalendarRange,
  Megaphone,
  UserPlus,
  Users,
} from 'lucide-react';
import { api } from '@/shared/api/axios';
import { Badge } from '@/shared/components/ui/Badge';
import { Card, CardBody, CardHeader } from '@/shared/components/ui/Card';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { formatINR } from '@/shared/utils/currency';
import { useAnnouncements } from '@/features/announcements/hooks/useAnnouncements';
import { ROUTES } from '@/routes/paths';
import { type StatTone } from '../components/StatTile';

// Gradient chip per status — same tokens StatTile / the dashboard StatCard use
// (tailwind.config.ts primary/success/warning/danger/info), no new colors.
const TONE_CHIP: Record<StatTone, string> = {
  primary: 'bg-gradient-to-br from-primary/20 to-primary/5 text-primary shadow-[0_0_0_1px_rgba(110,98,255,0.15)]',
  success: 'bg-gradient-to-br from-success/20 to-success/5 text-success shadow-[0_0_0_1px_rgba(16,185,129,0.15)]',
  warning: 'bg-gradient-to-br from-warning/20 to-warning/5 text-warning shadow-[0_0_0_1px_rgba(245,158,11,0.15)]',
  danger: 'bg-gradient-to-br from-danger/20 to-danger/5 text-danger shadow-[0_0_0_1px_rgba(239,68,68,0.15)]',
  info: 'bg-gradient-to-br from-info/20 to-info/5 text-info shadow-[0_0_0_1px_rgba(6,182,212,0.15)]',
};

interface HrOverview {
  headcount: { total: number; byDepartment: Record<string, number> };
  attendanceToday: { present: number; late: number; onLeave: number; notCheckedIn: number };
  leaves: {
    pendingCount: number;
    pending: {
      id: string;
      employee: string;
      department: string;
      type: string;
      fromDate: string;
      toDate: string;
      days: number;
      reason: string;
    }[];
  };
  payroll: { month: string; finalizedCount: number; draftCount: number; finalizedNetTotal: number };
  recruitment: { openRoles: number; draftRoles: number; inPipeline: number };
  recentJoiners: { id: string; name: string; department: string; role: string; joinedAt: string }[];
}

function useHrOverview() {
  return useQuery({
    queryKey: ['hr', 'overview'],
    queryFn: async () => (await api.get<HrOverview>('/api/hr/overview')).data,
    refetchInterval: 60_000,
  });
}

const DEPT_LABELS: Record<string, string> = {
  sales: 'Sales',
  hr: 'HR',
  accounts: 'Accounts',
  it: 'IT',
  support: 'Support',
  customer_support: 'Customer Support',
  digital_marketing: 'Digital Marketing',
  management: 'Management',
  unassigned: 'Unassigned',
};

function humanize(value: string): string {
  return DEPT_LABELS[value] ?? value.replaceAll('_', ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function StatCard({
  icon: Icon,
  label,
  value,
  subtitle,
  to,
  linkLabel,
  tone = 'primary',
  index = 0,
}: {
  icon: React.ElementType;
  label: string;
  value: string | number;
  subtitle?: string;
  to: string;
  linkLabel: string;
  tone?: StatTone;
  index?: number;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1], delay: index * 0.05 }}
      whileHover={{ y: -2 }}
      className="group flex flex-col gap-3 rounded-xl border border-outline bg-surface p-5 shadow-surface-1 transition-shadow duration-200 ease-standard hover:shadow-surface-3"
    >
      <div className="flex items-center gap-3">
        <span
          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition-transform duration-200 ease-standard group-hover:scale-110 ${TONE_CHIP[tone]}`}
        >
          <Icon className="h-5 w-5" aria-hidden />
        </span>
        <p className="text-body-sm font-medium text-on-surface-variant">{label}</p>
      </div>
      <p className="text-display-sm font-bold text-on-surface">{value}</p>
      <div className="flex items-center justify-between gap-2">
        {subtitle && <p className="truncate text-body-sm text-on-surface-variant">{subtitle}</p>}
        <Link to={to} className="inline-flex shrink-0 items-center gap-1 text-body-sm font-medium text-primary hover:underline">
          {linkLabel}
          <ArrowRight className="h-3 w-3" aria-hidden />
        </Link>
      </div>
    </motion.div>
  );
}

/**
 * HRMS home for HR users (spec §6): headcount, today's attendance, the leave
 * approval queue, current-month payroll and the recruitment pipeline — every
 * figure computed live from the same collections the section pages manage.
 */
export function HrDashboardPage() {
  const { data, isLoading, isError } = useHrOverview();

  if (isLoading) {
    return (
      <div className="p-4 md:p-6">
        <Skeleton className="h-[600px]" />
      </div>
    );
  }
  if (isError || !data) {
    return (
      <div className="p-6">
        <EmptyState
          title="Could not load the HR dashboard"
          description="This view is limited to HR and administrators. Check your connection or try refreshing."
        />
      </div>
    );
  }

  const departments = Object.entries(data.headcount.byDepartment).sort((a, b) => b[1] - a[1]);
  const maxDept = departments.length ? departments[0][1] : 1;

  return (
    <div className="flex h-full flex-col gap-5 overflow-y-auto p-4 md:p-6">
      <div className="flex items-center gap-3">
        <Users className="h-6 w-6 text-primary" aria-hidden />
        <div>
          <h1 className="text-display-sm font-semibold text-on-surface">HR Dashboard</h1>
          <p className="text-body-sm text-on-surface-variant">
            People, attendance, leave, payroll and hiring — one live view
          </p>
        </div>
      </div>

      {/* ── Headline stats ── */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          icon={Users}
          label="Active Employees"
          value={data.headcount.total}
          subtitle={`Across ${departments.length} department${departments.length !== 1 ? 's' : ''}`}
          to={ROUTES.hr}
          linkLabel="Team & Access"
          tone="primary"
          index={0}
        />
        <StatCard
          icon={CalendarClock}
          label="Present Today"
          value={data.attendanceToday.present}
          subtitle={`${data.attendanceToday.late} late · ${data.attendanceToday.onLeave} on leave · ${data.attendanceToday.notCheckedIn} not checked in`}
          to={ROUTES.attendance}
          linkLabel="Attendance"
          tone="success"
          index={1}
        />
        <StatCard
          icon={CalendarRange}
          label="Pending Leave Approvals"
          value={data.leaves.pendingCount}
          subtitle="Awaiting review"
          to={ROUTES.leaves}
          linkLabel="Leaves"
          tone="warning"
          index={2}
        />
        <StatCard
          icon={BriefcaseBusiness}
          label="Hiring Pipeline"
          value={data.recruitment.inPipeline}
          subtitle={`${data.recruitment.openRoles} open role${data.recruitment.openRoles !== 1 ? 's' : ''}${data.recruitment.draftRoles ? ` · ${data.recruitment.draftRoles} draft` : ''}`}
          to={ROUTES.recruitment}
          linkLabel="Recruitment"
          tone="info"
          index={3}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        {/* ── Leave approval queue ── */}
        <Card>
          <CardHeader
            title="Leave requests awaiting approval"
            action={
              <Link to={ROUTES.leaves} className="text-body-sm font-medium text-primary hover:underline">
                View all
              </Link>
            }
          />
          <CardBody>
            {data.leaves.pending.length === 0 ? (
              <p className="py-4 text-center text-body-sm text-on-surface-variant">
                No pending requests — the queue is clear.
              </p>
            ) : (
              <ul className="flex flex-col gap-2">
                {data.leaves.pending.map((leave) => (
                  <li
                    key={leave.id}
                    className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-outline p-3"
                  >
                    <div className="min-w-0">
                      <p className="font-medium text-on-surface">
                        {leave.employee}
                        <span className="ml-2 text-body-sm font-normal text-on-surface-variant">
                          {humanize(leave.department)}
                        </span>
                      </p>
                      <p className="text-body-sm text-on-surface-variant">
                        {leave.fromDate} → {leave.toDate} · {leave.days} day{leave.days !== 1 ? 's' : ''}
                        {leave.reason ? ` · ${leave.reason}` : ''}
                      </p>
                    </div>
                    <Badge tone="warning">{humanize(leave.type)}</Badge>
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>

        {/* ── Headcount by department ── */}
        <Card>
          <CardHeader
            title="Headcount by department"
            action={
              <Link to={ROUTES.organization} className="text-body-sm font-medium text-primary hover:underline">
                Organization
              </Link>
            }
          />
          <CardBody>
            {departments.length === 0 ? (
              <p className="py-4 text-center text-body-sm text-on-surface-variant">No active employees yet.</p>
            ) : (
              <ul className="flex flex-col gap-2.5">
                {departments.map(([dept, count]) => (
                  <li key={dept} className="flex items-center gap-3">
                    <span className="w-36 shrink-0 truncate text-body-sm text-on-surface">{humanize(dept)}</span>
                    <span className="h-2.5 flex-1 overflow-hidden rounded-full bg-surface-variant">
                      <span
                        className="block h-full rounded-full bg-primary"
                        style={{ width: `${Math.max(6, (count / maxDept) * 100)}%` }}
                      />
                    </span>
                    <span className="w-8 shrink-0 text-right text-body-sm font-semibold text-on-surface">{count}</span>
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>

        {/* ── Payroll this month ── */}
        <Card>
          <CardHeader
            title={`Payroll — ${data.payroll.month}`}
            action={
              <Link to={ROUTES.payroll} className="text-body-sm font-medium text-primary hover:underline">
                Payroll
              </Link>
            }
          />
          <CardBody className="grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-display-sm font-bold text-on-surface">{data.payroll.finalizedCount}</p>
              <p className="text-body-sm text-on-surface-variant">Finalized payslips</p>
            </div>
            <div>
              <p className="text-display-sm font-bold text-on-surface">{data.payroll.draftCount}</p>
              <p className="text-body-sm text-on-surface-variant">Draft payslips</p>
            </div>
            <div>
              <p className="text-display-sm font-bold text-on-surface">
                {data.payroll.finalizedNetTotal > 0 ? formatINR(data.payroll.finalizedNetTotal) : '—'}
              </p>
              <p className="text-body-sm text-on-surface-variant">Net pay (finalized)</p>
            </div>
          </CardBody>
        </Card>

        {/* ── Recent joiners ── */}
        <Card>
          <CardHeader
            title="Joined in the last 30 days"
            action={
              <Link to={ROUTES.hr} className="text-body-sm font-medium text-primary hover:underline">
                Team & Access
              </Link>
            }
          />
          <CardBody>
            {data.recentJoiners.length === 0 ? (
              <p className="py-4 text-center text-body-sm text-on-surface-variant">No new joiners in the last 30 days.</p>
            ) : (
              <ul className="flex flex-col gap-2">
                {data.recentJoiners.map((joiner) => (
                  <li key={joiner.id} className="flex items-center gap-3 rounded-lg border border-outline p-3">
                    <span className="rounded-full bg-success/10 p-2 text-success">
                      <UserPlus className="h-4 w-4" aria-hidden />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium text-on-surface">{joiner.name}</p>
                      <p className="text-body-sm text-on-surface-variant">
                        {humanize(joiner.department)} ·{' '}
                        {new Date(joiner.joinedAt).toLocaleDateString('en-IN', { dateStyle: 'medium' })}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>

        {/* ── Onboarding Pipeline ── */}
        <OnboardingPipelineCard />

        {/* ── Upcoming Birthdays & Anniversaries ── */}
        <UpcomingEventsCard />
      </div>
    </div>
  );
}

/* ── Onboarding Pipeline Widget ──────────────────────────────────────────── */

function OnboardingPipelineCard() {
  const { data, isLoading } = useQuery({
    queryKey: ['hr', 'onboarding-pipeline'],
    queryFn: async () => {
      const { data: result } = await api.get('/api/hr-onboarding-candidates/candidates?limit=200');
      const candidates = result?.data || [];
      const counts: Record<string, number> = { pending_info: 0, info_submitted: 0, docs_submitted: 0, verification: 0, completed: 0 };
      for (const c of candidates) { if (counts[c.onboardingStatus] !== undefined) counts[c.onboardingStatus]++; else counts.pending_info++; }
      return { total: candidates.length, ...counts };
    },
    staleTime: 30_000,
  });

  const stages = [
    { key: 'pending_info', label: 'Pending Info', color: 'bg-gray-400' },
    { key: 'info_submitted', label: 'Info Submitted', color: 'bg-blue-500' },
    { key: 'docs_submitted', label: 'Docs Submitted', color: 'bg-indigo-500' },
    { key: 'verification', label: 'Verification', color: 'bg-purple-500' },
    { key: 'completed', label: 'Completed', color: 'bg-green-500' },
  ];

  return (
    <Card>
      <CardHeader
        title="Onboarding Pipeline"
        action={
          <Link to={ROUTES.hrOnboarding} className="text-body-sm font-medium text-primary hover:underline">
            View all
          </Link>
        }
      />
      <CardBody>
        {isLoading ? (
          <Skeleton className="h-[100px]" />
        ) : !data || data.total === 0 ? (
          <p className="py-4 text-center text-body-sm text-on-surface-variant">No onboarding candidates yet. Hire someone from Recruitment to see them here.</p>
        ) : (
          <div className="space-y-3">
            <p className="text-body-sm text-on-surface-variant">{data.total} candidate{data.total !== 1 ? 's' : ''} in pipeline</p>
            <div className="flex h-3 w-full overflow-hidden rounded-full bg-surface-variant">
              {stages.map(s => {
                const count = (data as Record<string, number>)[s.key] || 0;
                const pct = data.total > 0 ? (count / data.total) * 100 : 0;
                return pct > 0 ? <div key={s.key} className={`${s.color} transition-all`} style={{ width: `${pct}%` }} title={`${s.label}: ${count}`} /> : null;
              })}
            </div>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {stages.map(s => {
                const count = (data as Record<string, number>)[s.key] || 0;
                return (
                  <div key={s.key} className="flex items-center gap-2 text-body-sm">
                    <span className={`h-2.5 w-2.5 rounded-full ${s.color}`} />
                    <span className="text-on-surface-variant">{s.label}</span>
                    <span className="ml-auto font-semibold text-on-surface">{count}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

/* ── Upcoming Birthdays, Work Anniversaries & Holidays ───────────────────── */

function UpcomingEventsCard() {
  const { data, isLoading } = useQuery({
    queryKey: ['hr', 'upcoming-events'],
    queryFn: async () => {
      const { data: result } = await api.get('/api/hr-performance/upcoming-events');
      return result as {
        birthdays: { name: string; date: string; department: string }[];
        anniversaries: { name: string; date: string; years: number; department: string }[];
        holidays: { name: string; date: string }[];
      };
    },
    staleTime: 5 * 60_000,
  });

  const { data: announcements } = useAnnouncements();
  const updates = announcements?.slice(0, 5) ?? [];

  return (
    <Card>
      <CardHeader title="Upcoming Events (Next 30 Days)" />
      <CardBody>
        {isLoading ? (
          <Skeleton className="h-[150px]" />
        ) : !data ? (
          <p className="py-4 text-center text-body-sm text-on-surface-variant">Could not load events.</p>
        ) : (
          <div className="space-y-4">
            {/* Upcoming Updates (Announcements) */}
            <div>
              <p className="text-body-sm font-semibold text-on-surface mb-1.5 flex items-center gap-1.5">
                <Megaphone className="h-4 w-4 text-primary" aria-hidden /> Upcoming Updates
              </p>
              {updates.length === 0 ? (
                <p className="text-body-sm text-on-surface-variant">No recent announcements.</p>
              ) : (
                <ul className="space-y-1">
                  {updates.map((update) => (
                    <li key={update.id} className="flex items-center justify-between gap-2 text-body-sm">
                      <span className="min-w-0 truncate text-on-surface">{update.title}</span>
                      <span className="shrink-0 text-on-surface-variant">
                        {new Date(update.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {/* Birthdays */}
            <div>
              <p className="text-body-sm font-semibold text-on-surface mb-1.5">🎂 Upcoming Birthdays</p>
              {data.birthdays.length === 0 ? (
                <p className="text-body-sm text-on-surface-variant">No upcoming birthdays.</p>
              ) : (
                <ul className="space-y-1">
                  {data.birthdays.map((b, i) => (
                    <li key={i} className="flex items-center justify-between text-body-sm">
                      <span className="text-on-surface">{b.name} <span className="text-on-surface-variant">({b.department})</span></span>
                      <span className="text-on-surface-variant">{new Date(b.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {/* Anniversaries */}
            <div>
              <p className="text-body-sm font-semibold text-on-surface mb-1.5">🎉 Work Anniversaries</p>
              {data.anniversaries.length === 0 ? (
                <p className="text-body-sm text-on-surface-variant">No upcoming anniversaries.</p>
              ) : (
                <ul className="space-y-1">
                  {data.anniversaries.map((a, i) => (
                    <li key={i} className="flex items-center justify-between text-body-sm">
                      <span className="text-on-surface">{a.name} <span className="text-on-surface-variant">({a.years} year{a.years !== 1 ? 's' : ''})</span></span>
                      <span className="text-on-surface-variant">{new Date(a.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {/* Holidays */}
            <div>
              <p className="text-body-sm font-semibold text-on-surface mb-1.5">🏖️ Upcoming Holidays</p>
              {data.holidays.length === 0 ? (
                <p className="text-body-sm text-on-surface-variant">No upcoming holidays.</p>
              ) : (
                <ul className="space-y-1">
                  {data.holidays.map((h, i) => (
                    <li key={i} className="flex items-center justify-between text-body-sm">
                      <span className="text-on-surface">{h.name}</span>
                      <span className="text-on-surface-variant">{new Date(h.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', weekday: 'short' })}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </CardBody>
    </Card>
  );
}
